/* eslint-disable react/prop-types */
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart } from 'echarts/charts';
import {
  DatasetComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { getColor } from 'helpers/utils';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { round } from 'lodash';
import { Card, Col, Form, Row } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { FiInfo } from 'react-icons/fi';
import Model from '../monitoring/Model';
import { isEmpty } from 'lodash';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer,
  LegendComponent,
  DatasetComponent
]);

const tooltipFormatter = (params, unit, colors, decimalPoint) => {
  const validParams = params.filter(
    param => param.value !== '' && param.value != null
  );

  if (validParams.length === 0) return '';

  const tooltipContent = validParams
    .map(param => {
      const value = param.value === 0 ? '0' : param.value.toFixed(decimalPoint);
      const color = param.color;

      return `
      <p class='mb-0 text-600 fs--1'>
        <span style='display: inline-block; width: 10px; height: 10px; border-radius: 50%; background-color: ${color}; margin-right: 5px;'></span>
        ${param.seriesName}: <span class='text-800 fw-semi-bold fs--1'>${value} <span class='text-500 fw-normal fs--1'>${unit}</span></span>
      </p>`;
    })
    .join('');

  return `
    ${
      validParams.length > 0
        ? `<h6 class="fs--2 mb-0 fw-semi-bold">${params[0].axisValueLabel}</h6>`
        : ''
    }
    <div class="align-items-center">${tooltipContent}</div>`;
};

const getOption = (result, date, unit, type, decimalPoint) => {
  // const colors = ['#5470C6', '#91CC75', '#EE6666', '#FF5733'];
  const colors = ['#5470C6', '#91CC75', '#ff4f4f', '#C966ED'];

  const legendData = Object.keys(result).map(key => key);
  const series = Object.entries(result).map(([key, values]) => ({
    name: key,
    type: type ? type : 'bar', // Use 'bar' type for simplicity, you can change it to 'line' if needed
    radius: ['40%', '70%'],
    symbolSize: 5,
    symbol: 'circle',
    data: values
    // barGap:0
    //  emphasis: {
    //   focus: 'series'
    // },
    // smooth:true
    // itemStyle: {
    //   borderRadius: [3, 3, 0, 0]
    // },
    // barWidth: '15px'
  }));

  return {
    color: colors,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: type == 'line' ? 'line' : 'shadow'
      },
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      // formatter: params => tooltipFormatter(params, unit, colors, decimalPoint)
      valueFormatter: function (value) {
        return value.toFixed(decimalPoint) + ` ${unit}`;
      }
    },
    grid: {
      right: '5%',
      left: '5%',
      bottom: '10%',
      containLabel: true
    },
    legend: {
      data: legendData,
      textStyle: {
        color: getColor('gray-700')
      },
      icon: 'circle',
      inactiveColor: getColor('gray-400')
    },
    xAxis: [
      {
        type: 'category',
        axisTick: {
          alignWithLabel: false
        },
        data: date
      }
    ],
    yAxis: [
      {
        type: 'value',
        axisLine: {
          show: true
        }
        // axisLabel: {
        //   formatter: `{value}`
        // }
      }
    ],
    series: series
  };
};

const MultiParamsBarChart = ({ chartData, type, info }) => {
  let results = chartData?.data;
  let date = chartData?.series;
  let unit = chartData?.unit;

  const [selectedYear, setSelectedYear] = useState(chartData.variant[0]);

  const [selectedYearData, setSelectedYearData] = useState(null); // Initialize as null
  const [showModal, setShowModal] = useState(false);

  const handleShowModal = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  let decimalPoint = 2;
  if (
    chartData?.label == 'Nitrogen Dioxide' ||
    chartData?.label == 'Carbon Monoxide' ||
    chartData?.label == 'Sulphur Dioxide' ||
    chartData?.label == 'Ozone'
  ) {
    decimalPoint = 5;
  }

  const handleSelectChange = event => {
    setSelectedYear(event.target.value);
  };

  useEffect(() => {
    const foundYearData = results[selectedYear] || {};
    setSelectedYearData(foundYearData);
  }, [chartData, selectedYear]);

  return (
    <>
      <Card className="h-100">
        <FalconCardHeader
          title={chartData?.label}
          light={true}
          titleTag="h5"
          desc={chartData?.desc}
          endEl={
            chartData.variant.length > 1 && (
              <Row className="align-items-center">
                <Col className="ps-2 py-0">
                  <Form.Select
                    value={selectedYear}
                    onChange={handleSelectChange}
                  >
                    {chartData.variant?.map((year, index) => (
                      <option key={index} value={year}>
                        {year}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
              </Row>
            )
          }
          infoBtn={!isEmpty(info) && <FiInfo onClick={handleShowModal} />}
        />
        <Card.Body className="h-100   position-relative" dir="ltr">
          {selectedYearData && (
            <ReactEChartsCore
              echarts={echarts}
              option={getOption(
                selectedYearData,
                date,
                unit,
                type,
                decimalPoint
              )}
              style={{ minHeight: '25.75rem' }}
              // key={key}
            />
          )}
          <p
            style={{
              fontSize: '12px',
              position: 'absolute',
              top: '20px',
              right: '20px'
            }}
          >
            {unit}
          </p>
        </Card.Body>
      </Card>
      <Model
        showModal={showModal}
        handleCloseModal={handleCloseModal}
        description={info || {}}
        title={chartData?.label}
      />
    </>
  );
};

export default MultiParamsBarChart;
